import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import { useDispatch, useSelector } from 'react-redux';
import { updateTotalCardsOnMove } from '../../redux/Slices/actionlistSlice';
import { showSuccess, showError } from '../../utils/snackbarHelpers';
import { config } from '../../config';
import ListViewGroupHeader from './ListViewGroupHeader';
import ListViewCardRow from './ListViewCardRow';
import ListViewColumnHeader from './ListViewColumnHeader';
import Add from '../icons/Add';
import Close from '../icons/Close';
import './scss/BoardListView.scss';

const COLUMNS = [
  { key: 'title', label: 'Name', flex: true, alwaysVisible: true, sortable: true },
  { key: 'members', label: 'Assignee', width: 100 },
  { key: 'dueDate', label: 'Due date', width: 100, sortable: true },
  { key: 'priority', label: 'Priority', width: 80, sortable: true },
  { key: 'status', label: 'Status', width: 120, sortable: true },
  { key: 'labels', label: 'Labels', width: 120 },
  { key: 'checklist', label: 'Checklist', width: 90 },
  { key: 'attachments', label: 'Files', width: 60 },
  { key: 'comments', label: 'Comments', width: 80 },
  { key: 'createdAt', label: 'Created', width: 90, sortable: true },
];

const PRIORITY_ORDER = { Urgent: 0, High: 1, Medium: 2, Normal: 3, Low: 4 };

const DEFAULT_VISIBLE_COLUMNS = ['members', 'dueDate', 'priority', 'status', 'labels', 'comments'];

const BoardListView = ({
  board,
  setBoard,
  boardId,
  fetchCardsForList,
  fetchMoreCardsForList,
  fetchedListsRef,
  hasLoadedInitialCards,
  isReadOnly,
  navbarHeight,
  initialVisibleColumns,
  onVisibleColumnsChange,
}) => {
  const dispatch = useDispatch();
  const scrollRef = useRef(null);
  const boardRef = useRef(board);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [stickyGroup, setStickyGroup] = useState(null);
  const stickyGroupIdRef = useRef(null);
  const [sortConfig, setSortConfig] = useState({ field: null, direction: 'asc' });
  const [visibleColumns, setVisibleColumns] = useState(
    new Set(initialVisibleColumns || DEFAULT_VISIBLE_COLUMNS)
  );

  // Sync if initialVisibleColumns arrives after mount (from API)
  const hasAppliedInitial = useRef(false);
  useEffect(() => {
    if (initialVisibleColumns && !hasAppliedInitial.current) {
      setVisibleColumns(new Set(initialVisibleColumns));
      hasAppliedInitial.current = true;
    }
  }, [initialVisibleColumns]);

  // Add card state: { listId, position: 'top' | 'bottom' } or null
  const [addingCard, setAddingCard] = useState(null);
  const [newCardTitle, setNewCardTitle] = useState('');
  const addCardInputRef = useRef(null);

  // Add list state
  const [showAddList, setShowAddList] = useState(false);
  const [newListTitle, setNewListTitle] = useState('');
  const addListInputRef = useRef(null);

  // Open inline add-card form for a given list at a position
  const handleStartAddCard = useCallback((listId, position = 'bottom') => {
    setAddingCard({ listId, position });
    setNewCardTitle('');
    // Expand the group if collapsed
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      next.delete(listId);
      return next;
    });
    setTimeout(() => addCardInputRef.current?.focus(), 0);
  }, []);

  // Submit new card
  const handleAddCard = useCallback(async (listId, position) => {
    const title = newCardTitle.trim();
    if (!title) return;

    const optimisticId = `optimistic-${Date.now()}`;
    const optimisticCard = {
      _id: optimisticId,
      actionList: { _id: listId },
      title,
      users: [],
      order: position === 'top' ? 0 : (board?.actionLists?.find((l) => l._id === listId)?.cards?.length || 0),
      isOptimistic: true,
      priority: 'Normal',
      labels: [],
      attachments: [],
      checklists: [],
      comments: [],
      commentCount: 0,
    };

    // Optimistic update
    setBoard((prev) => ({
      ...prev,
      actionLists: prev.actionLists.map((l) =>
        l._id === listId
          ? {
              ...l,
              cards: position === 'top'
                ? [optimisticCard, ...(l.cards || [])]
                : [...(l.cards || []), optimisticCard],
            }
          : l
      ),
    }));

    setNewCardTitle('');
    setTimeout(() => addCardInputRef.current?.focus(), 0);

    try {
      const response = await fetch(config.API_URI + '/v1/cards', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          actionListId: listId,
          title,
          position,
          users: [],
        }),
      });

      if (!response.ok) throw new Error('Failed to add card');
      const { card: newCard } = await response.json();

      // Replace optimistic card with server response
      setBoard((prev) => ({
        ...prev,
        actionLists: prev.actionLists.map((l) => {
          const idx = (l.cards || []).findIndex((c) => c._id === optimisticId);
          if (idx !== -1) {
            const updatedCards = [...l.cards];
            updatedCards[idx] = {
              ...newCard,
              actionList: { _id: l._id },
              isOptimisticallyMoved: false,
            };
            return { ...l, cards: updatedCards };
          }
          return l;
        }),
      }));
    } catch {
      // Rollback
      setBoard((prev) => ({
        ...prev,
        actionLists: prev.actionLists.map((l) => ({
          ...l,
          cards: (l.cards || []).filter((c) => c._id !== optimisticId),
        })),
      }));
      showError(dispatch, 'Failed to add card');
    }
  }, [newCardTitle, board?.actionLists, setBoard, dispatch]);

  const handleCancelAddCard = useCallback(() => {
    setAddingCard(null);
    setNewCardTitle('');
  }, []);

  const handleAddList = useCallback(async (e) => {
    e?.preventDefault();
    const title = newListTitle.trim();
    if (!title) return;

    const tempId = `temp-${Date.now()}`;
    const newList = { id: tempId, _id: tempId, title, cards: [], order: (board?.actionLists?.length || 0) + 1 };

    // Optimistic update
    setBoard((prev) => ({
      ...prev,
      actionLists: [...(prev.actionLists || []), newList],
    }));
    setNewListTitle('');
    setTimeout(() => addListInputRef.current?.focus(), 0);

    try {
      const response = await fetch(`${config.API_URI}/v1/actionLists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ boardId, title }),
      });

      if (!response.ok) throw new Error('Failed to add list');
      const data = await response.json();

      // Replace temp with server response
      setBoard((prev) => ({
        ...prev,
        actionLists: prev.actionLists.map((l) =>
          l.id === tempId ? data.actionList : l
        ),
      }));
      showSuccess(dispatch, 'List added');
    } catch {
      // Rollback
      setBoard((prev) => ({
        ...prev,
        actionLists: prev.actionLists.filter((l) => l.id !== tempId),
      }));
      showError(dispatch, 'Failed to add list');
    }
  }, [newListTitle, board?.actionLists?.length, boardId, setBoard, dispatch]);

  // Keep boardRef in sync for use in drop handler
  useEffect(() => {
    boardRef.current = board;
  }, [board]);

  // Flatten actionLists + cards into a single array for the virtualizer
  const flatItems = useMemo(() => {
    const items = [];
    if (!board?.actionLists) return items;

    board.actionLists.forEach((list) => {
      if (list.archived) return;
      const cards = list.cards || [];
      items.push({ type: 'GROUP_HEADER', list, listId: list._id, cardCount: cards.length });

      if (!collapsedGroups.has(list._id)) {
        // Add task top row (input shown when active)
        if (!isReadOnly) {
          items.push({ type: 'ADD_TASK_TOP', list, listId: list._id });
        }

        let visibleCards = cards.filter((c) => !c.archived);

        // Sort cards within each group
        if (sortConfig.field) {
          visibleCards = [...visibleCards].sort((a, b) => {
            let valA, valB;
            switch (sortConfig.field) {
              case 'title':
                valA = (a.title || '').toLowerCase();
                valB = (b.title || '').toLowerCase();
                return valA.localeCompare(valB);
              case 'dueDate':
                valA = a.dueDate?.date ? new Date(a.dueDate.date).getTime() : Infinity;
                valB = b.dueDate?.date ? new Date(b.dueDate.date).getTime() : Infinity;
                return valA - valB;
              case 'priority':
                valA = PRIORITY_ORDER[a.priority] ?? 3;
                valB = PRIORITY_ORDER[b.priority] ?? 3;
                return valA - valB;
              case 'status':
                return 0; // same list, same status
              case 'createdAt':
                valA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                valB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                return valA - valB;
              default:
                return 0;
            }
          });
          if (sortConfig.direction === 'desc') visibleCards.reverse();
        }

        visibleCards.forEach((card) => {
          items.push({ type: 'CARD_ROW', card, list });
        });

        // Empty-state placeholder when the list has no visible cards
        if (visibleCards.length === 0) {
          items.push({ type: 'EMPTY_STATE', list, listId: list._id });
        }

        // Add task footer row
        if (!isReadOnly) {
          items.push({ type: 'ADD_TASK_FOOTER', list, listId: list._id });
        }
      }
    });
    return items;
  }, [board?.actionLists, collapsedGroups, sortConfig, isReadOnly]);

  // Single vertical virtualizer
  const virtualizer = useVirtualizer({
    count: flatItems.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => {
      const item = flatItems[index];
      if (item?.type === 'GROUP_HEADER') return 48;
      if (item?.type === 'ADD_TASK_TOP') {
        return addingCard?.listId === item.listId && addingCard?.position === 'top' ? 44 : 0;
      }
      if (item?.type === 'ADD_TASK_FOOTER') return 36;
      if (item?.type === 'EMPTY_STATE') return 40;
      return 52;
    },
    overscan: 10,
  });

  // Sort handler: cycles none → asc → desc → none
  const handleSort = useCallback((field) => {
    setSortConfig((prev) => {
      if (prev.field === field) {
        if (prev.direction === 'asc') return { field, direction: 'desc' };
        return { field: null, direction: 'asc' };
      }
      return { field, direction: 'asc' };
    });
  }, []);

  // Column visibility toggle
  const handleToggleColumn = useCallback((key) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      // Persist to backend
      if (onVisibleColumnsChange) {
        onVisibleColumnsChange([...next]);
      }
      return next;
    });
  }, [onVisibleColumnsChange]);

  // Move action list (group reorder) — optimistic
  const moveActionList = useCallback((sourceId, targetId) => {
    setBoard((prevBoard) => {
      const updatedActionLists = [...prevBoard.actionLists];
      const sourceIndex = updatedActionLists.findIndex((l) => l._id === sourceId);
      const targetIndex = updatedActionLists.findIndex((l) => l._id === targetId);
      if (sourceIndex === -1 || targetIndex === -1) return prevBoard;

      const [movedList] = updatedActionLists.splice(sourceIndex, 1);
      updatedActionLists.splice(targetIndex, 0, movedList);

      const reorderedActionLists = updatedActionLists.map((list, index) => ({
        ...list,
        order: index + 1,
      }));
      return { ...prevBoard, actionLists: reorderedActionLists };
    });
  }, [setBoard]);

  // Persist list order to API
  const updateBoardOrder = useCallback(async () => {
    const actionListOrders = boardRef.current.actionLists.map((list) => ({
      _id: list._id,
      order: list?.order,
    }));
    try {
      await fetch(`${config.API_URI}/v1/boards/${boardId}/order`, {
        method: 'PUT',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ actionListOrders }),
      });
    } catch (error) {
      console.error('Error updating action list order:', error);
    }
  }, [boardId]);

  // Toggle group collapse
  const toggleCollapse = useCallback((listId) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(listId)) {
        next.delete(listId);
      } else {
        next.add(listId);
      }
      return next;
    });
  }, []);

  // Collapse all groups when dragging a group, restore on drop
  const preGroupDragCollapsedRef = useRef(null);

  const collapseAllGroups = useCallback(() => {
    setCollapsedGroups((prev) => {
      preGroupDragCollapsedRef.current = new Set(prev);
      const allIds = (board?.actionLists || []).filter((l) => !l.archived).map((l) => l._id);
      return new Set(allIds);
    });
  }, [board?.actionLists]);

  const restoreCollapsedGroups = useCallback(() => {
    if (preGroupDragCollapsedRef.current !== null) {
      setCollapsedGroups(preGroupDragCollapsedRef.current);
      preGroupDragCollapsedRef.current = null;
    }
  }, []);

  // Trigger card fetching when group headers become visible
  useEffect(() => {
    const virtualItems = virtualizer.getVirtualItems();
    virtualItems.forEach((vItem) => {
      const item = flatItems[vItem.index];
      if (item?.type === 'GROUP_HEADER' && item.listId) {
        if (!fetchedListsRef.current.has(item.listId)) {
          fetchedListsRef.current.add(item.listId);
          setTimeout(() => {
            fetchCardsForList(item.listId);
          }, 150);
        }
      }
    });
  }, [virtualizer.getVirtualItems().length, flatItems, fetchCardsForList, fetchedListsRef]);

  // Auto-load remaining cards in the background (lists initially fetch only 10)
  const actionListStates = useSelector(state => state.actionList.actionLists);
  const loadingMoreRef = useRef(new Set());
  const loadedBatchesRef = useRef(new Set());

  useEffect(() => {
    if (!board?.actionLists || !hasLoadedInitialCards) return;

    board.actionLists.forEach((list) => {
      if (list.archived) return;
      const listState = actionListStates[list._id];
      if (!listState || !listState.hasMore) return;

      const currentCount = (list.cards || []).length;
      if (currentCount >= listState.totalCards) return;

      const batchKey = `${list._id}-${currentCount}`;
      if (loadingMoreRef.current.has(list._id)) return;
      if (loadedBatchesRef.current.has(batchKey)) return;

      loadingMoreRef.current.add(list._id);
      loadedBatchesRef.current.add(batchKey);

      setTimeout(() => {
        fetchMoreCardsForList(list._id, currentCount).finally(() => {
          loadingMoreRef.current.delete(list._id);
        });
      }, 300);
    });
  }, [board?.actionLists, actionListStates, hasLoadedInitialCards, fetchMoreCardsForList]);

  // --- Drag and Drop ---

  // Apply an optimistic card move synchronously. The previous implementation
  // wrapped this in a 16ms setTimeout, which silently dropped the final move
  // when the user released the drag before the timer fired — leaving the
  // board state stale and the API call sending the wrong target position.
  const moveCard = useCallback((cardId, targetListId, targetPosition) => {
    setBoard((prevBoard) => {
      let cardToMove = null;
      let sourceListId = null;

      const updatedActionLists = prevBoard.actionLists.map((list) => {
        const newList = { ...list };
        if (newList.cards?.some((card) => card._id === cardId)) {
          sourceListId = newList._id;
          newList.cards = newList.cards.reduce((acc, card) => {
            if (card._id === cardId) {
              cardToMove = {
                ...card,
                actionList: { _id: targetListId },
                isOptimisticallyMoved: sourceListId !== targetListId,
              };
              return acc;
            }
            return [...acc, card];
          }, []);
        }
        return newList;
      });

      if (!cardToMove) return prevBoard;

      const finalActionLists = updatedActionLists.map((list) => {
        const newList = { ...list };
        if (newList._id === targetListId) {
          const newTargetCards = [...newList.cards];
          newTargetCards.splice(targetPosition, 0, cardToMove);
          newList.cards = newTargetCards.map((card, index) => ({
            ...card,
            order: index,
            actionList: { _id: targetListId },
          }));
        }
        return newList;
      });

      if (sourceListId) {
        finalActionLists.forEach((list) => {
          if (list._id === sourceListId) {
            list.cards = list.cards.map((card, index) => ({
              ...card,
              order: index,
            }));
          }
        });
      }

      return { ...prevBoard, actionLists: finalActionLists };
    });
  }, [setBoard]);

  // API call to persist card order
  const updateCardsOrder = useCallback(async (sourceListId, cardId, targetListId, targetPosition) => {
    try {
      await fetch(`${config.API_URI}/v1/boards/${boardId}/cards/order`, {
        method: 'PUT',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ sourceListId, cardId, targetListId, targetPosition }),
      });
    } catch (error) {
      console.error('Error moving card:', error);
    }
  }, [boardId]);

  // Monitor drops to finalize card moves and list reorders
  useEffect(() => {
    return monitorForElements({
      onDrop({ source, location }) {
        // Card drop → reorder (move) and persist
        if (source.data.type === 'CARD') {
          const movingCardId = source.data._id;
          const sourceListId = source.data.actionList?._id || source.data.actionList;

          // The innermost active drop target at drop time. Empty if the
          // card was released outside any registered target — in that
          // case we simply do nothing.
          const innermost = location.current.dropTargets[0];
          if (!innermost) return;
          if (innermost.data.type !== 'CARD') return;
          if (innermost.data._id === movingCardId) return;

          const edge = extractClosestEdge(innermost.data);
          if (!edge) return;

          const targetListId = innermost.data.actionList?._id;
          const targetOrder = innermost.data.order;
          const sourceOrder = source.data.order;

          // Compute the destination index. For same-list drops we have to
          // account for the source slot disappearing when the card is
          // removed from its current position.
          let newOrder = edge === 'top' ? targetOrder : targetOrder + 1;
          if (sourceListId === targetListId && sourceOrder < newOrder) {
            newOrder -= 1;
          }

          // No-op: dropping back into the same slot.
          if (sourceListId === targetListId && newOrder === sourceOrder) return;

          // Apply the move locally (optimistic) and persist to the server.
          moveCard(movingCardId, targetListId, newOrder);
          if (sourceListId !== targetListId) {
            dispatch(updateTotalCardsOnMove({ sourceListId, targetListId }));
          }
          updateCardsOrder(sourceListId, movingCardId, targetListId, newOrder);
        }

        // List drop → persist list order
        if (source.data.type === 'ACTIONLIST') {
          updateBoardOrder();
        }
      },
    });
  }, [dispatch, moveCard, updateCardsOrder, updateBoardOrder]);

  // Auto-scroll during drag
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    return autoScrollForElements({
      element: el,
      getConfiguration: () => ({ maxScrollSpeed: 'fast', allowedAxis: 'vertical' }),
    });
  }, []);

  // Track sticky group header based on scroll position
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let rafId;
    const onScroll = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const scrollTop = el.scrollTop;
        if (scrollTop < 10) {
          if (stickyGroupIdRef.current) {
            stickyGroupIdRef.current = null;
            setStickyGroup(null);
          }
          return;
        }

        let cumHeight = 0;
        let currentGroup = null;

        for (let i = 0; i < flatItems.length; i++) {
          const item = flatItems[i];
          const height = item.type === 'GROUP_HEADER' ? 48 : 52;
          if (cumHeight > scrollTop + 100) break;
          if (item.type === 'GROUP_HEADER' && cumHeight + height <= scrollTop + 5) {
            currentGroup = item;
          }
          cumHeight += height;
        }

        const newId = currentGroup?.listId || null;
        if (stickyGroupIdRef.current !== newId) {
          stickyGroupIdRef.current = newId;
          setStickyGroup(currentGroup);
        }
      });
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [flatItems]);

  return (
    <div
      className="board-list-view"
      ref={scrollRef}
      // style={{ height: `calc(100vh - ${navbarHeight + 75}px)` }}
    >
      {/* Sticky column header */}
      <div className="list-view-sticky-column-header">
        <ListViewColumnHeader
          columns={COLUMNS}
          visibleColumns={visibleColumns}
          sortConfig={sortConfig}
          onSort={handleSort}
          onToggleColumn={handleToggleColumn}
        />
      </div>

      {/* Sticky group header */}
      {stickyGroup && (
        <div className="list-view-sticky-header">
          <ListViewGroupHeader
            list={stickyGroup.list}
            isCollapsed={collapsedGroups.has(stickyGroup.listId)}
            onToggleCollapse={() => toggleCollapse(stickyGroup.listId)}
            cardCount={stickyGroup.cardCount}
            moveCard={moveCard}
            moveActionList={moveActionList}
            setBoard={setBoard}
            isReadOnly={isReadOnly}
            boardId={boardId}
            workspaceId={board?.wShortId}
            collapseAllGroups={collapseAllGroups}
            restoreCollapsedGroups={restoreCollapsedGroups}
            onAddCard={() => handleStartAddCard(stickyGroup.listId, 'top')}
          />
        </div>
      )}

      <div
        className="board-list-view-inner"
        style={{
          height: virtualizer.getTotalSize(),
          position: 'relative',
        }}
      >
        {virtualizer.getVirtualItems().map((virtualItem) => {
          const item = flatItems[virtualItem.index];
          if (!item) return null;

          return (
            <div
              key={virtualItem.key}
              data-index={virtualItem.index}
              ref={virtualizer.measureElement}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              {item.type === 'GROUP_HEADER' ? (
                <ListViewGroupHeader
                  list={item.list}
                  isCollapsed={collapsedGroups.has(item.listId)}
                  onToggleCollapse={() => toggleCollapse(item.listId)}
                  cardCount={item.cardCount}
                  moveCard={moveCard}
                  moveActionList={moveActionList}
                  setBoard={setBoard}
                  isReadOnly={isReadOnly}
                  boardId={boardId}
                  workspaceId={board?.wShortId}
                  collapseAllGroups={collapseAllGroups}
                  restoreCollapsedGroups={restoreCollapsedGroups}
                  onAddCard={() => handleStartAddCard(item.listId, 'top')}
                />
              ) : item.type === 'ADD_TASK_TOP' ? (
                addingCard?.listId === item.listId && addingCard?.position === 'top' ? (
                  <div className="list-view-add-task-row">
                    <div className="add-task-input-row">
                      <input
                        ref={addCardInputRef}
                        className="add-task-input"
                        placeholder="Task name"
                        value={newCardTitle}
                        onChange={(e) => setNewCardTitle(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddCard(item.listId, 'top');
                          }
                          if (e.key === 'Escape') handleCancelAddCard();
                        }}
                        onBlur={() => {
                          setTimeout(() => {
                            if (!newCardTitle.trim()) handleCancelAddCard();
                          }, 150);
                        }}
                        autoFocus
                      />
                    </div>
                  </div>
                ) : null
              ) : item.type === 'ADD_TASK_FOOTER' ? (
                <div className="list-view-add-task-row">
                  {addingCard?.listId === item.listId && addingCard?.position === 'bottom' ? (
                    <div className="add-task-input-row">
                      <input
                        ref={addCardInputRef}
                        className="add-task-input"
                        placeholder="Task name"
                        value={newCardTitle}
                        onChange={(e) => setNewCardTitle(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddCard(item.listId, 'bottom');
                          }
                          if (e.key === 'Escape') handleCancelAddCard();
                        }}
                        onBlur={() => {
                          setTimeout(() => {
                            if (!newCardTitle.trim()) handleCancelAddCard();
                          }, 150);
                        }}
                        autoFocus
                      />
                    </div>
                  ) : (
                    <button
                      className="add-task-btn"
                      onClick={() => handleStartAddCard(item.listId, 'bottom')}
                    >
                      <Add size={16} color="var(--secondary-text-color)" />
                      <span>Add task</span>
                    </button>
                  )}
                </div>
              ) : item.type === 'EMPTY_STATE' ? (
                <div className="list-view-empty-row">
                  <span className="list-view-empty-text">No tasks here yet</span>
                </div>
              ) : (
                <ListViewCardRow
                  card={item.card}
                  list={item.list}
                  moveCard={moveCard}
                  setBoard={setBoard}
                  isReadOnly={isReadOnly}
                  visibleColumns={visibleColumns}
                  boardId={boardId}
                  boardRef={boardRef}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Add list */}
      {!isReadOnly && (
        <div className="list-view-add-list">
          {showAddList ? (
            <form className="add-list-form" onSubmit={handleAddList}>
              <input
                ref={addListInputRef}
                className="add-list-input"
                placeholder="Enter list title..."
                value={newListTitle}
                onChange={(e) => setNewListTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setShowAddList(false);
                    setNewListTitle('');
                  }
                }}
                autoFocus
              />
              <div className="add-list-actions">
                <button type="submit" className="add-list-submit">Add list</button>
                <span
                  className="add-list-cancel"
                  onClick={() => { setShowAddList(false); setNewListTitle(''); }}
                >
                  <Close size={18} color="var(--secondary-text-color)" />
                </span>
              </div>
            </form>
          ) : (
            <button className="add-list-btn" onClick={() => setShowAddList(true)}>
              <Add size={16} color="var(--secondary-text-color)" />
              <span>Add another list</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default BoardListView;
